<template>
  <div class="action-bar__summary">
    <div class="action-bar__summary-box action-bar__summary-box--product">
      <div class="action-bar__summary-product">
        <div class="action-bar__summary-product-image">
          <media
            v-if="mainProductImage"
            :media="mainProductImage"
            :cols="[3, 3, 6]"
            :lazyload="true"
          />
        </div>
        <div class="action-bar__summary-product-details">
          <h4 class="action-bar__summary-product-title">
            <span>{{ mainProduct?.decoratedProduct?.family }}</span>
            <span>{{ mainProduct?.decoratedProduct?.modelName }}</span>
          </h4>
          <div class="action-bar__summary-product-price-amount">
            <div class="action-bar__summary-product-meta">
              <amount-input
                :model-value="mainProduct?.quantity"
                readonly
                :label="
                  $textDictionary['pdp.amount.label'] ?? 'pdp.amount.label'
                "
              />
            </div>
            <div
              v-if="mainProduct?.decoratedProduct?.priceText"
              class="action-bar__summary-product-price"
            >
              <price-element
                :current-price="mainProduct.decoratedProduct.priceText"
                alignment="left"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Products -->
      <div
        v-for="(item, index) in additionalProducts"
        :key="index"
        class="action-bar__summary-product"
      >
        <div class="action-bar__summary-product-image">
          <media
            v-if="getProductImage(item.decoratedProduct)"
            :media="getProductImage(item.decoratedProduct)"
            :cols="[3, 3, 6]"
            :lazyload="true"
          />
        </div>
        <div class="action-bar__summary-product-details">
          <h4 class="action-bar__summary-product-title">
            <span>{{ item.decoratedProduct?.modelName }}</span>
          </h4>
          <div class="action-bar__summary-product-price-amount">
            <div class="action-bar__summary-product-meta">
              <amount-input
                :model-value="item?.quantity"
                readonly
                :label="
                  $textDictionary['pdp.amount.label'] ?? 'pdp.amount.label'
                "
              />
            </div>
            <div
              v-if="item?.decoratedProduct?.priceText"
              class="action-bar__summary-product-price"
            >
              <price-element
                :current-price="item.decoratedProduct.priceText"
                alignment="left"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="action-bar__summary-box action-bar__summary-box--cta">
      <div class="action-bar__summary-cart">
        <div v-if="cartStore.total" class="action-bar__summary-cart-subtotal">
          <span class="action-bar__summary-cart-subtotal-label">
            {{
              $textDictionary['cart.label.subtotal'] ?? 'cart.label.subtotal'
            }}
          </span>
          <span class="action-bar__summary-cart-subtotal-value">
            {{ formatMoney(cartStore.totalExcludingTax, $locale) }}
          </span>
        </div>
        <div class="action-bar__summary-cart-actions">
          <Button
            :style-type="displayGoToCheckoutButton ? 'secondary' : 'attraction'"
            size="medium"
            :expand="true"
            @click="$emit('go-to-cart')"
          >
            {{
              $textDictionary['actionBar.shoppingCartButton'] ??
              'actionBar.shoppingCartButton'
            }}
          </Button>
          <Button
            v-if="displayGoToCheckoutButton"
            style-type="attraction"
            size="medium"
            :expand="true"
            @click="$emit('go-to-checkout')"
          >
            {{ checkoutButtonText }}
          </Button>
        </div>
        <SecurePaymentInfo />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Button,
  Media,
  PriceElement,
  AmountInput
} from '@hypercodestudio/basler-components';
import SecurePaymentInfo from '../SecurePaymentInfo.vue';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { formatMoney } from '~/utils/shop/formatMoney';
import { CartTypes } from '~/stores/cartStore/model';
import useModernCheckout from '~/utils/shop/useModernCheckout';

const { $textDictionary, $locale, $globalPageSettings } = useNuxtApp();
const cartStore = useCartStore();

type CartItemInfo = {
  quantity: number;
  decoratedProduct: DecoratedProduct | any;
  config_id?: string;
};

interface Props {
  productSku: string;
  priceText: string;
  cartItems: readonly CartItemInfo[];
}

const props = defineProps<Props>();

const checkoutButtonText = computed(() => {
  return cartStore.activeCartType === CartTypes.CART
    ? cartStore.numberOfArticlesCart === 1
      ? $textDictionary.value[
          'actionBar.checkoutButton.singleProduct'
        ]?.replace(
          '{numberOfArticles}',
          String(cartStore.numberOfArticlesCart)
        ) ?? 'actionBar.checkoutButton.singleProduct'
      : $textDictionary.value[
          'actionBar.checkoutButton.multipleProducts'
        ]?.replace(
          '{numberOfArticles}',
          String(cartStore.numberOfArticlesCart)
        ) ?? 'actionBar.checkoutButton.multipleProducts'
    : cartStore.activeCartType === CartTypes.CUSTOMER_CART
    ? cartStore.numberOfArticlesCustomerCart === 1
      ? $textDictionary.value[
          'actionBar.checkoutButton.singleProduct'
        ]?.replace(
          '{numberOfArticles}',
          String(cartStore.numberOfArticlesCustomerCart)
        ) ?? 'actionBar.checkoutButton.singleProduct'
      : $textDictionary.value[
          'actionBar.checkoutButton.multipleProducts'
        ]?.replace(
          '{numberOfArticles}',
          String(cartStore.numberOfArticlesCustomerCart)
        ) ?? 'actionBar.checkoutButton.multipleProducts'
    : 'actionBar.checkoutButton.multipleProducts';
});

// Hauptprodukt aus cartItems finden
const mainProduct = computed(() => {
  const item = props.cartItems.find((item) => {
    return item.decoratedProduct?.sku === props.productSku;
  });

  if (item) {
    return {
      decoratedProduct: item.decoratedProduct,
      quantity: item.quantity
    };
  }
  return null;
});

// Zusatzprodukte aus cartItems filtern
const additionalProducts = computed(() => {
  return props.cartItems.filter((item) => {
    return item.decoratedProduct?.sku !== props.productSku;
  });
});

// Funktion zum Generieren von Produktbildern mit Fallback
function getProductImage(decoratedProduct: DecoratedProduct | any) {
  if (!decoratedProduct) {
    return null;
  }

  return decoratedProduct.getProductImage?.(
    {
      sizes: '200px',
      widths: [400, 600, 800, 1000],
      aspectRatio: [4, 3]
    },
    $globalPageSettings.value?.defaultProductImage ?? undefined
  );
}

// Bild für das Hauptprodukt generieren
const mainProductImage = computed(() => {
  return getProductImage(mainProduct.value?.decoratedProduct);
});

const displayGoToCheckoutButton = computed(() => useModernCheckout());

defineEmits<{
  'go-to-cart': [];
  'go-to-checkout': [];
}>();
</script>

<style scoped></style>
